
import React, { useState, useEffect } from 'react';
import { 
  Truck, ShieldCheck, Zap, Smartphone, 
  ChevronRight, Check, 
  Box, Terminal,
  Cpu, 
  Instagram, Linkedin, Twitter, 
  Target, Activity, 
  FileCheck, 
  X, 
  Package,
  FileCode, 
  Landmark, 
  FileDown, 
  Users, 
  Wrench, Menu,
  Radio
} from 'lucide-react';
import { Plan } from './types.ts';
import { ContactSection } from './ContactSection.tsx';
import { Badge, Card } from './SharedComponents.tsx';
import ModernHero from './components/ui/modern-hero.tsx';

// LandingPage Component
export const LandingPage = ({ plans, onLoginClick, onRegisterClick, onMasterAccess }: { plans: Plan[], onLoginClick: () => void, onRegisterClick: (planId?: string) => void, onMasterAccess: () => void }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [_activeLegalDoc, setActiveLegalDoc] = useState<string | null>(null);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const masterFeatures = [
    { icon: Target, title: "CRM Kanban", desc: "Gestão visual de leads e pipeline comercial." },
    { icon: FileCode, title: "NFS-e Automática", desc: "Emissão de notas fiscais sem intervenção manual." },
    { icon: Landmark, title: "Controladoria", desc: "Auditoria de lucro real e custos de campo." },
    { icon: FileDown, title: "PDF Engine", desc: "Gerador de propostas e contratos profissionais." },
    { icon: Box, title: "Gestão de Ativos", desc: "Controle de estoque com rastreabilidade total." },
    { icon: Wrench, title: "Módulo Oficina", desc: "Gestão de manutenção e quarentena de itens." },
    { icon: Users, title: "Escala de Equipes", desc: "Distribuição inteligente de squads externos." },
    { icon: Activity, title: "Analytics Real", desc: "BI de faturamento e produtividade da frota." },
  ];

  return (
    <div className="min-h-screen bg-white text-secondary selection:bg-primary selection:text-white font-sans overflow-x-hidden">
      
      {/* Header Fixo */}
      <header className={`fixed top-0 w-full z-[100] transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-100 py-3 shadow-sm' : 'bg-transparent py-6 md:py-10'}`}>
        <nav className="max-w-[1440px] mx-auto px-8 md:px-12 flex justify-between items-center">
          <div className="flex items-center gap-4 group cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className="bg-primary p-2.5 rounded-xl text-white shadow-xl shadow-primary/20 group-hover:rotate-12 transition-transform shrink-0">
              <Truck size={22} className="md:w-7 md:h-7" strokeWidth={2.5} />
            </div>
            <span className={`text-xl md:text-2xl font-black uppercase tracking-tighter shrink-0 italic ${scrolled ? 'text-secondary' : 'text-white'}`}>EventLog <span className="text-primary not-italic">PRO</span></span>
          </div>
          
          <div className={`hidden lg:flex items-center gap-12 text-[10px] font-black uppercase tracking-[0.25em] ${scrolled ? 'text-gray-400' : 'text-white/50'}`}>
            <button onClick={() => scrollToSection('features')} className="hover:text-primary transition-colors">Infraestrutura</button>
            <button onClick={() => scrollToSection('workflow')} className="hover:text-primary transition-colors">Logística</button>
            <button onClick={() => scrollToSection('ecosystem')} className="hover:text-primary transition-colors">Ecossistema</button>
            <button onClick={() => scrollToSection('pricing')} className="hover:text-primary transition-colors">Planos</button>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={onLoginClick} className={`hidden sm:block text-[10px] font-black uppercase tracking-widest px-4 py-3 hover:text-primary transition-colors ${scrolled ? 'text-secondary' : 'text-white'}`}>Acessar</button>
            <button onClick={() => onRegisterClick()} className="hidden md:block bg-secondary text-white px-8 py-4 rounded-full text-[10px] font-black uppercase tracking-widest shadow-2xl hover:bg-primary transition-all active:scale-95 border-b-4 border-black/20">
              Provisionar Agora
            </button>
            <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-3 text-secondary hover:bg-gray-100 rounded-xl transition-all">
               <Menu size={28} />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Sidebar Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[200] lg:hidden">
           <div className="absolute inset-0 bg-secondary/60 backdrop-blur-md" onClick={() => setMobileMenuOpen(false)}></div>
           <div className="absolute right-0 top-0 h-full w-[320px] bg-white shadow-2xl p-10 flex flex-col gap-10 animate-in slide-in-from-right duration-300">
              <div className="flex justify-between items-center">
                 <span className="text-xs font-black uppercase tracking-widest text-primary italic">Navegação Core</span>
                 <button onClick={() => setMobileMenuOpen(false)} className="p-2.5 bg-gray-50 rounded-xl text-secondary"><X size={24} /></button>
              </div>
              <div className="flex flex-col gap-8 text-[12px] font-black uppercase tracking-widest">
                 <button onClick={() => scrollToSection('features')} className="text-left py-2 border-b border-gray-50 flex justify-between items-center group">Infraestrutura <ChevronRight size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-all" /></button>
                 <button onClick={() => scrollToSection('workflow')} className="text-left py-2 border-b border-gray-50 flex justify-between items-center group">Fluxo Logístico <ChevronRight size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-all" /></button>
                 <button onClick={() => scrollToSection('ecosystem')} className="text-left py-2 border-b border-gray-50 flex justify-between items-center group">Recursos SaaS <ChevronRight size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-all" /></button>
                 <button onClick={() => scrollToSection('pricing')} className="text-left py-2 border-b border-gray-50 flex justify-between items-center group">Planos e Preços <ChevronRight size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-all" /></button>
                 <button onClick={onLoginClick} className="text-left py-2 text-slate-400">Login de Unidade</button>
              </div>
              <div className="mt-4">
                 <button 
                  onClick={() => { setMobileMenuOpen(false); onRegisterClick(); }}
                  className="w-full bg-primary text-white py-6 rounded-[24px] font-black uppercase text-[11px] tracking-widest shadow-xl shadow-primary/20 flex items-center justify-center gap-3 active:scale-95"
                 >
                   Provisionar Agora <ChevronRight size={18} />
                 </button>
              </div>
              <div className="mt-auto pt-8 border-t border-gray-100 text-center">
                 <p className="text-[9px] font-black text-gray-300 uppercase tracking-[0.4em] leading-relaxed">Desenvolvido pela <span className="text-primary underline decoration-primary/30">M3 Nexus</span></p>
              </div>
           </div>
        </div>
      )}

      <main>
        <ModernHero onStartClick={() => scrollToSection('pricing')} onLearnMore={() => scrollToSection('features')} />

        <section id="features" className="py-16 md:py-32 bg-white px-6">
          <div className="max-w-[1400px] mx-auto">
             <header className="text-center mb-12 md:mb-20">
                <Badge className="bg-primary/10 text-primary border-none px-6 py-2 font-black text-[10px] uppercase tracking-widest mb-4">Core Engine</Badge>
                <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tighter italic text-secondary leading-tight">Infraestrutura <span className="text-primary">Master.</span></h2>
             </header>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <FeatureCard icon={Radio} title="Radar Live GPRS" desc="Rastreamento em tempo real with telemetria avançada, status de motor e link de rastro para o cliente." tag="TRACKING" />
                <FeatureCard icon={Cpu} title="Auditoria Gemini IA" desc="Análise automatizada de avarias estruturais através de visão computacional. Redução de perdas de material." tag="INTELLIGENCE" highlight />
                <FeatureCard icon={Smartphone} title="App do Montador" desc="Protocolo de carga digital, checklist fotográfico e colheita de assinatura offline com sincronização automática." tag="FIELD OPS" />
             </div>
          </div>
        </section>

        <section id="workflow" className="py-16 md:py-32 bg-slate-50 px-6">
          <div className="max-w-[1400px] mx-auto">
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
                <div className="space-y-6 md:space-y-8 order-2 lg:order-1">
                   <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tighter italic text-secondary leading-none">O Fluxo da <br className="hidden sm:block" /> <span className="text-primary">Performance.</span></h2>
                   <p className="text-gray-500 font-medium text-base md:text-lg leading-relaxed max-w-xl">Deixe o papel no passado. Sincronize o comercial com a logística em um workflow blindado contra erros operacionais.</p>
                   <div className="space-y-3 md:space-y-4">
                      <WorkflowItem icon={FileCheck} label="Proposta aprovada gera OS automática" />
                      <WorkflowItem icon={Package} label="Terminal de expedição live no galpão" />
                      <WorkflowItem icon={Truck} label="Roteirização e despacho via App GPS" />
                      <WorkflowItem icon={ShieldCheck} label="Checkout auditado por IA e assinatura" />
                   </div>
                </div>
                <div className="relative order-1 lg:order-2 px-4 md:px-10">
                   <div className="bg-secondary p-8 md:p-10 rounded-[40px] md:rounded-[60px] shadow-2xl relative overflow-hidden aspect-square flex items-center justify-center">
                      <div className="absolute inset-0 opacity-10"><Terminal className="w-full h-full" /></div>
                      <div className="relative z-10 text-center space-y-4 md:space-y-6">
                         <div className="w-20 h-20 md:w-32 md:h-32 bg-primary rounded-[28px] md:rounded-[40px] flex items-center justify-center mx-auto shadow-2xl animate-bounce">
                            <Zap size={40} className="text-white md:w-16 md:h-16" />
                         </div>
                         <h3 className="text-white text-xl md:text-2xl font-black uppercase italic">Core Sync Ativo</h3>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </section>

        <section id="ecosystem" className="py-16 md:py-32 bg-white px-6">
          <div className="max-w-[1400px] mx-auto">
            <header className="text-center mb-12 md:mb-20">
               <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tighter italic text-secondary leading-tight">Ecossistema <br className="xs:hidden" /> <span className="text-primary">SaaS Completo.</span></h2>
               <p className="text-gray-400 font-medium mt-4 text-sm md:text-base">Tudo o que sua locadora precisa para escalar a operação.</p>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {masterFeatures.map((f, i) => (
                <div key={i} className="p-6 md:p-8 rounded-[32px] md:rounded-[36px] bg-gray-50 border border-transparent hover:border-primary/20 hover:bg-white transition-all group shadow-sm hover:shadow-xl">
                   <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-white flex items-center justify-center text-primary shadow-sm mb-6 group-hover:bg-primary group-hover:text-white transition-all shrink-0">
                      <f.icon size={20} className="md:w-6 md:h-6" />
                   </div>
                   <h4 className="text-xs md:text-sm font-black uppercase tracking-tight text-secondary mb-2 leading-tight">{f.title}</h4>
                   <p className="text-[10px] font-bold text-gray-400 uppercase leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="py-16 md:py-32 bg-slate-50 px-6 relative overflow-hidden">
          <div className="max-w-[1400px] mx-auto">
             <header className="text-center mb-12 md:mb-20">
                <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tighter italic text-secondary leading-tight">Escolha sua <span className="text-primary">Escala.</span></h2>
             </header>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {plans.map((plan) => (
                  <Card key={plan.id} className={`p-8 md:p-10 border-4 transition-all flex flex-col items-center text-center rounded-[40px] md:rounded-[48px] ${plan.popular ? 'border-primary bg-white md:scale-105 z-10 shadow-2xl' : 'border-white/5 bg-white text-secondary'}`}>
                    <h4 className="text-xl md:text-2xl font-black uppercase tracking-tight mb-6 md:mb-8">{plan.name}</h4>
                    <div className="flex items-start justify-center gap-1 mb-8 md:mb-10">
                       <span className="text-xs md:text-sm font-bold opacity-40 mt-2">R$</span>
                       <span className="text-5xl md:text-6xl font-black tracking-tighter">{plan.price}</span>
                       <span className="text-xs md:text-sm font-bold opacity-40 mt-8">/mês</span>
                    </div>
                    <div className="space-y-3 md:space-y-4 flex-1 mb-10 text-left w-full">
                       {plan.features.slice(0, 6).map(f => (
                         <div key={f} className="flex items-center gap-3">
                            <Check size={14} className="text-primary shrink-0" />
                            <span className="text-[10px] md:text-xs font-bold uppercase tracking-tight opacity-70 leading-tight">{f}</span>
                         </div>
                       ))}
                    </div>
                    <button onClick={() => onRegisterClick(plan.id)} className={`w-full py-4 md:py-5 rounded-[20px] md:rounded-[24px] font-black uppercase text-[10px] md:text-[11px] tracking-widest transition-all active:scale-95 ${plan.popular ? 'bg-primary text-white shadow-xl shadow-primary/20' : 'bg-secondary text-white'}`}>Assinar {plan.name}</button>
                  </Card>
                ))}
             </div>
          </div>
        </section>

        <ContactSection />
      </main>

      <footer className="bg-secondary text-white py-20 px-6 border-t border-white/5 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto relative z-10 flex flex-col items-center text-center">
           
           {/* Branding Centralizado - Limpo e Focado */}
           <div className="space-y-6 mb-16">
              <div className="flex items-center justify-center gap-4 group cursor-default">
                 <div className="bg-primary p-3 rounded-2xl text-white shadow-xl shadow-primary/20 group-hover:rotate-12 transition-transform shrink-0"><Truck size={32} /></div>
                 <span className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic">EventLog <span className="text-primary not-italic">PRO</span></span>
              </div>
              <p className="text-white/40 text-sm md:text-base font-medium leading-relaxed max-w-lg mx-auto italic">
                 A inteligência por trás das maiores empresas de locação e infraestrutura do Brasil. <br className="hidden md:block" /> Digitalize sua logística e proteja seu lucro real com o Core Engine v4.5.
              </p>
              
              {/* Social Minimalista */}
              <div className="flex justify-center gap-6 pt-4">
                 <button className="text-white/20 hover:text-primary transition-colors"><Instagram size={20} /></button>
                 <button className="text-white/20 hover:text-primary transition-colors"><Linkedin size={20} /></button>
                 <button className="text-white/20 hover:text-primary transition-colors"><Twitter size={20} /></button>
              </div>
           </div>

           {/* Créditos M3 Nexus - Base Final */}
           <div className="w-full pt-12 border-t border-white/5 flex flex-col items-center gap-8">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                 <span className="text-[10px] md:text-xs font-bold text-white/20 uppercase tracking-widest italic">Sistema desenvolvido por</span>
                 <span className="text-xs md:text-sm font-black uppercase italic text-primary underline underline-offset-8 decoration-2 whitespace-nowrap tracking-tighter">M3 Nexus Sistemas Inteligentes</span>
              </div>
              <div className="flex flex-col items-center gap-1 opacity-20">
                <p className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.6em] cursor-pointer hover:opacity-100 transition-opacity" onClick={onMasterAccess}>© 2026 • Core Engine v4.5 Stable • Sorocaba - SP</p>
              </div>
           </div>
        </div>
      </footer>

      {/* MODAL JURÍDICO DINÂMICO */}
      {_activeLegalDoc && (
        <div className="fixed inset-0 z-[200] bg-secondary/95 backdrop-blur-md flex items-center justify-center p-4 md:p-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-[32px] md:rounded-[40px] w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95">
             <div className="p-6 md:p-10 bg-gray-50 border-b border-gray-100 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-4 min-w-0">
                   <div className="p-2.5 md:p-3 bg-primary/10 text-primary rounded-2xl shrink-0">
                      <ShieldCheck size={24} />
                   </div>
                   <div className="min-w-0">
                      <h2 className="text-lg md:text-2xl font-black uppercase text-secondary italic tracking-tight truncate">Documentação Legal</h2>
                      <p className="text-[8px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">SaaS Master Agreement</p>
                   </div>
                </div>
                <button onClick={() => setActiveLegalDoc(null)} className="p-2 hover:bg-gray-100 rounded-xl transition-all active:scale-90 shrink-0"><X size={24} /></button>
             </div>
             <div className="flex-1 overflow-y-auto p-6 md:p-12 scrollbar-hide text-secondary">
                <p>Conteúdo jurídico do EventLog PRO para parceiros homologados.</p>
             </div>
             <div className="p-6 md:p-8 bg-gray-50 border-t border-gray-100 flex justify-end shrink-0">
                <button onClick={() => setActiveLegalDoc(null)} className="w-full sm:w-auto px-10 py-4 bg-secondary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl active:scale-95 transition-all">Fechar</button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc, tag, highlight }: any) => (
  <Card className={`p-6 md:p-8 rounded-[36px] md:rounded-[44px] border-2 transition-all hover:shadow-2xl flex flex-col h-full ${highlight ? 'border-primary bg-primary/5' : 'border-gray-50 bg-white'}`}>
    <div className={`p-3.5 md:p-4 rounded-2xl w-fit mb-6 md:mb-8 ${highlight ? 'bg-primary text-white shadow-xl' : 'bg-gray-50 text-primary'}`}><Icon size={24} className="md:w-7 md:h-7" /></div>
    <Badge className="bg-gray-100 text-gray-500 border-none text-[8px] mb-4 w-fit">{tag}</Badge>
    <h3 className="text-lg md:text-xl font-black uppercase italic mb-3 leading-tight">{title}</h3>
    <p className="text-gray-500 text-xs md:text-sm font-medium leading-relaxed">{desc}</p>
  </Card>
);

const WorkflowItem = ({ icon: Icon, label }: any) => (
  <div className="flex items-center gap-3 md:gap-4 p-4 md:p-5 rounded-[24px] md:rounded-[28px] bg-white border border-gray-100 shadow-sm hover:border-primary transition-all">
    <div className="p-2 md:p-3 bg-primary/10 text-primary rounded-xl shrink-0"><Icon size={18} className="md:w-5 md:h-5" /></div>
    <span className="text-[9px] md:text-xs font-black uppercase tracking-widest text-secondary leading-tight">{label}</span>
  </div>
);
